import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Receipt, DollarSign, Download, FileText, RefreshCw, Search, Eye, X, Filter, Calendar, User, Trash2, Package, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PatientSearchDropdown, FixedChargeSearchDropdown } from '@/components/SearchDropdowns';
import api from '@/lib/api';
import { toast } from 'sonner';
import { downloadCSV, downloadExcel, EXPORT_COLUMNS } from '@/lib/exportUtils';

export default function Billing() {
  const [billingRecords, setBillingRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientDropdownRef = useRef(null);
  
  // Fixed charges state
  const [fixedCharges, setFixedCharges] = useState([]);
  
  // Filter states
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPatient, setFilterPatient] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  
  const [formData, setFormData] = useState({
    patient_id: '',
    items: [{ description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [patientsRes, billsRes, visitsRes, fixedChargesRes] = await Promise.all([
        api.get('/patients'),
        api.get('/billing'),
        api.get('/visits'),
        api.get('/fixed-charges')
      ]);
      setPatients(patientsRes.data);
      setFixedCharges(fixedChargesRes.data || []);
      
      // Create maps for quick lookup
      const patientMap = {};
      patientsRes.data.forEach(p => { patientMap[p.id] = p; });
      
      const visitMap = {};
      visitsRes.data.forEach(v => { visitMap[v.id] = v; });
      
      const billsWithPatients = billsRes.data.map(bill => ({
        ...bill,
        patient: patientMap[bill.patient_id] || { full_name: 'Unknown', patient_number: 'N/A' },
        visit: visitMap[bill.visit_id] || null
      }));
      
      setBillingRecords(billsWithPatients);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Get unique patients from billing records for filter dropdown
  const uniquePatients = useMemo(() => {
    const patientMap = new Map();
    billingRecords.forEach(bill => {
      if (bill.patient?.id && !patientMap.has(bill.patient.id)) {
        patientMap.set(bill.patient.id, bill.patient);
      }
    });
    return Array.from(patientMap.values()).sort((a, b) => 
      (a.full_name || '').localeCompare(b.full_name || '')
    );
  }, [billingRecords]);

  // Filter billing records based on all filters
  const filteredRecords = useMemo(() => {
    return billingRecords.filter(bill => {
      // Search query filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          bill.invoice_number?.toLowerCase().includes(query) ||
          bill.visit_id?.toLowerCase().includes(query) ||
          bill.visit?.visit_number?.toLowerCase().includes(query) ||
          bill.patient?.full_name?.toLowerCase().includes(query) ||
          bill.patient?.patient_number?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filterStatus && bill.payment_status !== filterStatus) {
        return false;
      }
      
      // Patient filter
      if (filterPatient && bill.patient?.id !== filterPatient) {
        return false;
      }
      
      // Date range filter
      if (filterDateFrom || filterDateTo) {
        const billDate = new Date(bill.created_at);
        if (filterDateFrom && billDate < new Date(filterDateFrom)) {
          return false;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (billDate > toDate) {
            return false;
          }
        }
      }
      
      // Amount range filter
      if (filterAmountMin && bill.total_amount < parseFloat(filterAmountMin)) {
        return false;
      }
      if (filterAmountMax && bill.total_amount > parseFloat(filterAmountMax)) {
        return false;
      }
      
      return true;
    });
  }, [billingRecords, searchQuery, filterStatus, filterPatient, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax]);

  // Filter patients for autosuggest dropdown
  const filteredPatients = useMemo(() => {
    if (!patientSearch.trim()) return patients.slice(0, 10); // Show first 10 if no search
    const query = patientSearch.toLowerCase();
    return patients.filter(p => 
      p.full_name?.toLowerCase().includes(query) ||
      p.patient_number?.toLowerCase().includes(query) ||
      p.phone?.includes(query)
    ).slice(0, 10); // Limit to 10 results
  }, [patients, patientSearch]);

  // Get selected patient name for display
  const selectedPatient = useMemo(() => {
    return patients.find(p => p.id === formData.patient_id);
  }, [patients, formData.patient_id]);

  // Click outside handler for patient dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterPatient('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterAmountMin('');
    setFilterAmountMax('');
  };

  const hasActiveFilters = searchQuery || filterStatus || filterPatient || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax;

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }]
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'amount') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      newItems[index][field] = parseInt(value) || 1;
    } else if (field === 'tax_percentage') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  // Handle fixed charge selection from dropdown
  const handleSelectFixedCharge = (charge) => {
    // Add as a new item to the invoice
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: charge.charge_name,
        amount: charge.default_rate,
        quantity: 1,
        tax_percentage: charge.tax_percentage,
        is_fixed_charge: true,
        fixed_charge_id: charge.id,
        category: charge.category
      }]
    });
    toast.success(`Added: ${charge.charge_name}`);
  };

  // Calculate totals
  const calculateTotals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    
    formData.items.forEach(item => {
      const itemTotal = (item.amount || 0) * (item.quantity || 1);
      const itemTax = itemTotal * ((item.tax_percentage || 0) / 100);
      subtotal += itemTotal;
      totalTax += itemTax;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: totalTax.toFixed(2),
      total: (subtotal + totalTax).toFixed(2)
    };
  }, [formData.items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate fixed charge items have quantity > 0
    const invalidItems = formData.items.filter(
      item => item.is_fixed_charge && (!item.quantity || item.quantity <= 0)
    );
    
    if (invalidItems.length > 0) {
      toast.error('Fixed charge items must have a quantity greater than 0');
      return;
    }
    
    // Validate all items have description and amount
    const emptyItems = formData.items.filter(
      item => !item.description.trim() || item.amount <= 0
    );
    
    if (emptyItems.length > 0) {
      toast.error('All items must have a description and valid amount');
      return;
    }
    
    try {
      // Format items for submission
      const formattedItems = formData.items.map(item => ({
        description: item.description,
        amount: item.amount * (item.quantity || 1),
        quantity: item.quantity || 1,
        unit_price: item.amount,
        tax_percentage: item.tax_percentage || 0,
        tax_amount: (item.amount * (item.quantity || 1)) * ((item.tax_percentage || 0) / 100),
        is_fixed_charge: item.is_fixed_charge || false,
        fixed_charge_id: item.fixed_charge_id || null,
        category: item.category || null
      }));
      
      await api.post('/billing', {
        ...formData,
        items: formattedItems
      });
      toast.success('Invoice created successfully!');
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        items: [{ description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }],
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handlePayment = async () => {
    if (!selectedBill || !paymentAmount) return;
    try {
      await api.put(`/billing/${selectedBill.id}/payment?amount=${parseFloat(paymentAmount)}`);
      toast.success('Payment recorded successfully!');
      setPaymentDialogOpen(false);
      setSelectedBill(null);
      setPaymentAmount('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleExportCSV = () => {
    const exportData = filteredRecords.map(bill => ({
      invoice_number: bill.invoice_number,
      patient_name: bill.patient?.full_name || 'Unknown',
      patient_id: bill.patient?.patient_number || 'N/A',
      visit_number: bill.visit?.visit_number || 'N/A',
      total_amount: bill.total_amount,
      paid_amount: bill.paid_amount,
      balance: bill.balance,
      payment_status: bill.payment_status,
      created_at: new Date(bill.created_at).toLocaleDateString(),
      notes: bill.notes || ''
    }));
    
    downloadCSV(exportData, EXPORT_COLUMNS.billing, 'billing_records');
  };

  const handleExportExcel = () => {
    const exportData = filteredRecords.map(bill => ({
      invoice_number: bill.invoice_number,
      patient_name: bill.patient?.full_name || 'Unknown',
      patient_id: bill.patient?.patient_number || 'N/A',
      visit_number: bill.visit?.visit_number || 'N/A',
      total_amount: bill.total_amount,
      paid_amount: bill.paid_amount,
      balance: bill.balance,
      payment_status: bill.payment_status,
      created_at: new Date(bill.created_at).toLocaleDateString(),
      notes: bill.notes || ''
    }));
    
    downloadExcel(exportData, EXPORT_COLUMNS.billing, 'billing_records');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="billing-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Billing
            </h1>
            <p className="text-slate-600 mt-2">Manage invoices and payments</p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-billing-button">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleExportCSV}>
                  <FileText className="mr-2 h-4 w-4" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel}>
                  <FileText className="mr-2 h-4 w-4" />
                  Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-invoice-button" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                <Plus className="mr-2 h-4 w-4" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Invoice</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="patient_id">Patient *</Label>
                  <div className="relative" ref={patientDropdownRef}>
                    <div 
                      className="flex items-center border rounded-md cursor-pointer hover:border-gray-400 transition-colors"
                    >
                      <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
                      <Input
                        placeholder="Search patient by name, ID, or phone..."
                        value={showPatientDropdown ? patientSearch : (selectedPatient ? `${selectedPatient.full_name} (${selectedPatient.patient_number})` : '')}
                        onChange={(e) => {
                          setPatientSearch(e.target.value);
                          setShowPatientDropdown(true);
                        }}
                        onClick={() => setShowPatientDropdown(true)}
                        className="pl-10 pr-10 border-0 focus-visible:ring-0 cursor-pointer"
                        data-testid="invoice-patient-search"
                      />
                      <ChevronDown 
                        className={`absolute right-3 h-4 w-4 text-gray-400 transition-transform cursor-pointer ${showPatientDropdown ? 'rotate-180' : ''}`} 
                        onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                      />
                    </div>
                    
                    {showPatientDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-y-auto">
                        {filteredPatients.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            No patients found
                          </div>
                        ) : (
                          filteredPatients.map((patient) => (
                            <button
                              key={patient.id}
                              type="button"
                              className={`w-full px-4 py-3 text-left hover:bg-purple-50 border-b last:border-b-0 flex items-center justify-between transition-colors ${
                                formData.patient_id === patient.id ? 'bg-purple-50' : ''
                              }`}
                              onClick={() => {
                                setFormData({...formData, patient_id: patient.id});
                                setPatientSearch('');
                                setShowPatientDropdown(false);
                              }}
                              data-testid={`patient-option-${patient.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <User className="w-4 h-4 text-purple-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{patient.full_name}</p>
                                  <p className="text-xs text-gray-500">
                                    {patient.patient_number} {patient.phone && `• ${patient.phone}`}
                                  </p>
                                </div>
                              </div>
                              {formData.patient_id === patient.id && (
                                <Check className="w-4 h-4 text-purple-600" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {/* Hidden required field for form validation */}
                  <input type="hidden" value={formData.patient_id} required />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Invoice Items *</Label>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={handleAddItem}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Custom Item
                      </Button>
                    </div>
                  </div>
                  
                  {/* Add Fixed Charge Section */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <Label className="text-sm font-medium text-purple-800 mb-2 block">
                      <Package className="h-4 w-4 inline mr-2" />
                      Add Fixed Charge from Master
                    </Label>
                    <FixedChargeSearchDropdown
                      charges={fixedCharges}
                      onSelect={handleSelectFixedCharge}
                      placeholder="Search fixed charges (e.g., Room Rent, Nursing Care)..."
                    />
                  </div>

                  {/* Invoice Items List */}
                  {formData.items.map((item, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${item.is_fixed_charge ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {item.is_fixed_charge && (
                          <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                            <Package className="h-3 w-3 mr-1" />
                            Fixed Charge
                          </Badge>
                        )}
                        {item.category && (
                          <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                        )}
                        <div className="ml-auto">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-5">
                          <Label className="text-xs text-slate-500">Description</Label>
                          <Input
                            placeholder="Item description"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            data-testid={`item-description-${index}`}
                            readOnly={item.is_fixed_charge}
                            className={item.is_fixed_charge ? 'bg-white/50' : ''}
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-slate-500">Rate (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={item.amount}
                            onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                            data-testid={`item-amount-${index}`}
                            readOnly={item.is_fixed_charge}
                            className={item.is_fixed_charge ? 'bg-white/50 font-mono' : 'font-mono'}
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-slate-500">Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            data-testid={`item-quantity-${index}`}
                            required
                          />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs text-slate-500">Tax%</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            placeholder="0"
                            value={item.tax_percentage}
                            onChange={(e) => handleItemChange(index, 'tax_percentage', e.target.value)}
                            readOnly={item.is_fixed_charge}
                            className={item.is_fixed_charge ? 'bg-white/50' : ''}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-slate-500">Total</Label>
                          <div className="h-10 px-3 py-2 bg-slate-100 rounded-md font-mono text-sm flex items-center justify-end">
                            ₹{((item.amount || 0) * (item.quantity || 1) * (1 + (item.tax_percentage || 0) / 100)).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <Button type="button" variant="outline" onClick={handleAddItem} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Custom Item
                  </Button>
                </div>

                {/* Invoice Summary */}
                <div className="p-4 bg-slate-100 rounded-lg">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Subtotal:</span>
                      <span className="font-mono">₹{calculateTotals.subtotal}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Tax:</span>
                      <span className="font-mono">₹{calculateTotals.tax}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-bold">
                      <span>Grand Total:</span>
                      <span className="font-mono text-lg text-purple-700">₹{calculateTotals.total}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
                  />
                </div>

                <Button type="submit" data-testid="submit-invoice-button" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  Create Invoice
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters Section */}
        <Card className="mb-6 border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="font-medium text-slate-700">Filters</span>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-slate-500 hover:text-slate-700">
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Invoice #, Patient name, Visit ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="billing-search-input"
                  />
                </div>
              </div>
              
              {/* Payment Status */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Payment Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_status">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Patient */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Patient</Label>
                <Select value={filterPatient} onValueChange={setFilterPatient}>
                  <SelectTrigger data-testid="filter-patient">
                    <SelectValue placeholder="All Patients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_patients">All Patients</SelectItem>
                    {uniquePatients.map(patient => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date From */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Date From</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  data-testid="filter-date-from"
                />
              </div>
              
              {/* Date To */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Date To</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  data-testid="filter-date-to"
                />
              </div>
              
              {/* Amount Min */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Min Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={filterAmountMin}
                  onChange={(e) => setFilterAmountMin(e.target.value)}
                  data-testid="filter-amount-min"
                />
              </div>
              
              {/* Amount Max */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Max Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={filterAmountMax}
                  onChange={(e) => setFilterAmountMax(e.target.value)}
                  data-testid="filter-amount-max"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-600">
            Showing {filteredRecords.length} of {billingRecords.length} invoices
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>

        {/* Billing Records */}
        <div className="space-y-4">
          {filteredRecords.length === 0 ? (
            <Card className="border border-slate-200">
              <CardContent className="p-12 text-center">
                <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {hasActiveFilters ? 'No billing records match your filters' : 'No billing records found'}
                </p>
                {hasActiveFilters && (
                  <Button variant="link" onClick={clearFilters} className="mt-2 text-[#4A238F]">
                    Clear filters
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredRecords.map((bill) => (
              <motion.div
                key={bill.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Card 
                  className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => {
                    setSelectedBill(bill);
                    setViewDialogOpen(true);
                  }}
                  data-testid={`billing-card-${bill.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                          <User className="h-6 w-6 text-amber-700" />
                        </div>
                        <div className="flex-1">
                          {/* Patient Name - Primary */}
                          <h3 className="font-bold text-lg text-slate-900 mb-1">
                            {bill.patient?.full_name || 'Unknown Patient'}
                          </h3>
                          
                          {/* Invoice Number - Secondary */}
                          <div className="flex items-center gap-2 mb-2">
                            <Receipt className="h-4 w-4 text-slate-400" />
                            <span className="font-medium text-slate-700">{bill.invoice_number}</span>
                            <Badge className={
                              bill.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                              bill.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                              'bg-rose-100 text-rose-700'
                            }>
                              {bill.payment_status}
                            </Badge>
                          </div>
                          
                          {/* Additional Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-slate-500">Patient ID:</span>
                              <p className="font-medium text-slate-700">{bill.patient?.patient_number || 'N/A'}</p>
                            </div>
                            {bill.visit && (
                              <div>
                                <span className="text-slate-500">Visit:</span>
                                <p className="font-medium text-slate-700">{bill.visit.visit_number}</p>
                              </div>
                            )}
                            <div>
                              <span className="text-slate-500">Total:</span>
                              <p className="font-bold text-slate-900">${bill.total_amount.toFixed(2)}</p>
                            </div>
                            {bill.balance > 0 && (
                              <div>
                                <span className="text-slate-500">Balance:</span>
                                <p className="font-bold text-rose-600">${bill.balance.toFixed(2)}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Date */}
                          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(bill.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedBill(bill);
                            setViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {bill.balance > 0 && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedBill(bill);
                              setPaymentDialogOpen(true);
                            }}
                            data-testid={`record-payment-${bill.id}`}
                            className="bg-[#0f766e] hover:bg-[#115e59] text-white"
                          >
                            <DollarSign className="mr-1 h-4 w-4" />
                            Pay
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </div>

        {/* View Billing Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-amber-600" />
                Invoice Details
              </DialogTitle>
            </DialogHeader>
            {selectedBill && (
              <div className="space-y-4">
                {/* Header Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">{selectedBill.invoice_number}</h3>
                    <Badge className={
                      selectedBill.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      selectedBill.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }>
                      {selectedBill.payment_status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-slate-600">Patient</p>
                      <p className="font-medium">{selectedBill.patient?.full_name}</p>
                      <p className="text-xs text-slate-500">{selectedBill.patient?.patient_number}</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Date</p>
                      <p className="font-medium">{new Date(selectedBill.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {selectedBill.visit && (
                    <div className="mt-2 text-sm">
                      <p className="text-slate-600">Visit</p>
                      <p className="font-medium">{selectedBill.visit.visit_number}</p>
                    </div>
                  )}
                </div>

                {/* Line Items */}
                <div>
                  <h4 className="font-medium mb-2">Invoice Items</h4>
                  <div className="border rounded-lg divide-y">
                    {selectedBill.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between p-3">
                        <span className="text-slate-700">{item.description}</span>
                        <span className="font-medium">${item.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="p-4 bg-indigo-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Amount:</span>
                    <span className="font-semibold text-slate-900">${selectedBill.total_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Paid Amount:</span>
                    <span className="font-semibold text-emerald-600">${selectedBill.paid_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-slate-700 font-medium">Balance Due:</span>
                    <span className="font-bold text-rose-600">${selectedBill.balance.toFixed(2)}</span>
                  </div>
                </div>

                {selectedBill.notes && (
                  <div>
                    <p className="text-sm text-slate-600">Notes</p>
                    <p className="text-slate-900">{selectedBill.notes}</p>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                    Close
                  </Button>
                  {selectedBill.balance > 0 && (
                    <Button
                      onClick={() => {
                        setViewDialogOpen(false);
                        setPaymentDialogOpen(true);
                      }}
                      className="bg-[#0f766e] hover:bg-[#115e59] text-white"
                    >
                      <DollarSign className="mr-1 h-4 w-4" />
                      Record Payment
                    </Button>
                  )}
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            {selectedBill && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600">Invoice: {selectedBill.invoice_number}</p>
                  <p className="text-sm text-slate-600">Balance: ${selectedBill.balance.toFixed(2)}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_amount">Payment Amount *</Label>
                  <Input
                    id="payment_amount"
                    type="number"
                    step="0.01"
                    max={selectedBill.balance}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    data-testid="payment-amount-input"
                    placeholder="0.00"
                    required
                  />
                </div>
                <Button
                  onClick={handlePayment}
                  data-testid="submit-payment-button"
                  className="w-full bg-[#0f766e] hover:bg-[#115e59] text-white"
                >
                  Record Payment
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
